"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StandardTargetProvider = void 0;
const board_1 = require("@creature-chess/board");
const getStats_1 = require("../../utils/getStats");
const getLivingEnemies_1 = require("../utils/getLivingEnemies");
const getTargetAttackPositions_1 = require("../utils/getTargetAttackPositions");
class StandardTargetProvider {
    /**
     * @inheritdoc
     */
    getTarget(board, pieceRegistry, combatStore, creatures, attackerId) {
        const piece = pieceRegistry.getPieceById(attackerId);
        if (!piece) {
            return null;
        }
        const attackRange = (0, getStats_1.getAttackRange)(piece, creatures);
        const enemies = (0, getLivingEnemies_1.getLivingEnemies)(piece, board, pieceRegistry, combatStore);
        if (enemies.length === 0) {
            return null;
        }
        const attackerPos = board.getPiecePosition(piece.id);
        if (!attackerPos) {
            return null;
        }
        const enemyDeltas = this.getEnemiesInAttackRange(board, piece.id, enemies, attackerPos, attackRange);
        if (enemyDeltas.length === 0) {
            return null;
        }
        return this.chooseTarget(enemyDeltas, combatStore.getPiece(piece.id).facingAway, creatures).id;
    }
    getEnemiesInAttackRange(board, attackerId, enemies, attackerPosition, attackRange) {
        const enemyDeltas = [];
        for (const enemy of enemies) {
            const enemyPosition = board.getPiecePosition(enemy.id);
            if (!enemyPosition) {
                continue;
            }
            // find all positions from which we can attack
            const attackPositions = (0, getTargetAttackPositions_1.getTargetAttackPositions)({ width: board.width, height: board.height }, (0, board_1.packPosition)(enemyPosition[0], enemyPosition[1]), attackRange);
            const emptyPositions = attackPositions.filter((position) => {
                const p = board.getPieceIdAtPosition((0, board_1.unpackX)(position), (0, board_1.unpackY)(position));
                return p === null || p === attackerId;
            });
            if (emptyPositions.length === 0) {
                continue;
            }
            emptyPositions.sort((a, b) => {
                const ax = (0, board_1.unpackX)(a) - attackerPosition[0];
                const ay = (0, board_1.unpackY)(a) - attackerPosition[1];
                const bx = (0, board_1.unpackX)(b) - attackerPosition[0];
                const by = (0, board_1.unpackY)(b) - attackerPosition[1];
                return ax * ax + ay * ay - (bx * bx + by * by);
            });
            enemyDeltas.push({
                enemy,
                enemyPosition: (0, board_1.packPosition)(enemyPosition[0], enemyPosition[1]),
                attackPosition: emptyPositions[0],
                delta: (0, board_1.getDelta)(emptyPositions[0], (0, board_1.packPosition)(attackerPosition[0], attackerPosition[1])),
            });
        }
        return enemyDeltas;
    }
    chooseTarget(enemies, facingNorth, creatures) {
        var _a, _b;
        if (enemies.length === 0) {
            throw new Error("No enemies provided to chooseTarget");
        }
        const getSquaredDistance = (delta) => delta.x * delta.x + delta.y * delta.y;
        const isInFacingDirection = (delta) => facingNorth
            ? (0, board_1.unpackY)(delta.enemyPosition) < (0, board_1.unpackY)(delta.attackPosition)
            : (0, board_1.unpackY)(delta.enemyPosition) > (0, board_1.unpackY)(delta.attackPosition);
        const costs = new Map();
        for (const { enemy } of enemies) {
            costs.set(enemy.id, (_b = (_a = creatures.get(enemy.definitionId)) === null || _a === void 0 ? void 0 : _a.cost) !== null && _b !== void 0 ? _b : 0);
        }
        enemies.sort((a, b) => {
            var _a, _b;
            // 1. Primary sort: Distance (closest first)
            const distanceDiff = getSquaredDistance(a.delta) - getSquaredDistance(b.delta);
            if (distanceDiff !== 0) {
                return distanceDiff;
            }
            // 2. Secondary sort: Cost (highest first)
            const costDiff = ((_a = costs.get(b.enemy.id)) !== null && _a !== void 0 ? _a : 0) - ((_b = costs.get(a.enemy.id)) !== null && _b !== void 0 ? _b : 0);
            if (costDiff !== 0) {
                return costDiff;
            }
            // 3. Tertiary sort: Facing direction preference
            const aFacing = isInFacingDirection(a);
            const bFacing = isInFacingDirection(b);
            if (aFacing !== bFacing) {
                return aFacing ? -1 : 1;
            }
            // 4. Final tiebreaker: ID (deterministic)
            return a.enemy.id < b.enemy.id ? -1 : 1;
        });
        return enemies[0].enemy;
    }
}
exports.StandardTargetProvider = StandardTargetProvider;
